import type { ComponentType } from "react";
import type { IconName } from "../ui/Icon";

/** Size class a widget adapts its internals to. */
export type SizeVariant = "compact" | "standard" | "expanded";

export interface WidgetSize {
  /** Grid units (wall layout). */
  w: number;
  h: number;
  /** Rendered pixels. */
  width: number;
  height: number;
  variant: SizeVariant;
}

export interface WidgetProps<C> {
  instanceId: string;
  config: C;
  size: WidgetSize;
  /** True while the dashboard is in edit mode: widgets should avoid capturing taps. */
  editing: boolean;
  /** True when rendered in the full-screen overlay. */
  fullscreen: boolean;
  openFullscreen: () => void;
}

export type SettingField<C> =
  | { key: keyof C & string; label: string; type: "select"; options: { value: string | number; label: string }[]; help?: string }
  | { key: keyof C & string; label: string; type: "toggle"; help?: string }
  | { key: keyof C & string; label: string; type: "number"; min: number; max: number; step?: number; help?: string }
  | { key: keyof C & string; label: string; type: "text"; maxLength: number; placeholder?: string; help?: string };

/**
 * The widget contract. Definitions are plain typed modules registered in
 * registry.ts — no runtime plugin loader — but the shape is the stable
 * interface future widgets (and providers) plug into.
 */
export interface WidgetDefinition<C extends object = Record<string, unknown>> {
  type: string;
  version: number;
  name: string;
  description: string;
  icon: IconName;
  category: "time" | "calendar" | "weather" | "household";
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
  maxSize?: { w: number; h: number };
  /** Height in px when stacked on a phone (may depend on config). */
  stackHeight: number | ((config: C) => number);
  defaults: C;
  settings: SettingField<C>[];
  /** Data capability this widget consumes (informational; used by diagnostics/docs). */
  provider?: string;
  capabilities?: { requiresAI?: boolean; requiresLocation?: boolean; requiresAuth?: boolean };
  supportsFullscreen?: boolean;
  /** "card" (default) draws a glass panel; "bare" sits directly on the scenery (hero clock, greeting). */
  chrome?: "card" | "bare" | ((config: C) => "card" | "bare");
  /** Upgrade config written by an older version. */
  migrate?: (config: Record<string, unknown>, fromVersion: number) => Record<string, unknown>;
  Component: ComponentType<WidgetProps<C>>;
}

/** Merge stored config over defaults and coerce each field to its schema. */
export function resolveConfig<C extends object>(def: WidgetDefinition<C>, stored: Record<string, unknown>, storedVersion: number): C {
  const migrated = def.migrate && storedVersion < def.version ? def.migrate(stored, storedVersion) : stored;
  const out = { ...def.defaults } as Record<string, unknown>;
  for (const field of def.settings) {
    const v = migrated[field.key];
    if (v === undefined) continue;
    switch (field.type) {
      case "select":
        if (field.options.some((o) => o.value === v)) out[field.key] = v;
        break;
      case "toggle":
        if (typeof v === "boolean") out[field.key] = v;
        break;
      case "number":
        if (typeof v === "number" && Number.isFinite(v)) out[field.key] = Math.min(field.max, Math.max(field.min, v));
        break;
      case "text":
        if (typeof v === "string") out[field.key] = v.slice(0, field.maxLength);
        break;
    }
  }
  return out as C;
}

export function stackHeightOf<C extends object>(def: WidgetDefinition<C>, config: C): number {
  return typeof def.stackHeight === "function" ? def.stackHeight(config) : def.stackHeight;
}

export function chromeOf<C extends object>(def: WidgetDefinition<C>, config: C): "card" | "bare" {
  return typeof def.chrome === "function" ? def.chrome(config) : (def.chrome ?? "card");
}

export function sizeVariant(width: number, height: number): SizeVariant {
  if (width < 340 || height < 190) return "compact";
  if (width > 760 && height > 460) return "expanded";
  return "standard";
}
